/**
 * @jsxRuntime classic
 * @jsx jsx
 * @jsxFrag jsx
 */

import { useCallback, useLayoutEffect, useState } from 'react';

import throttle from 'lodash/throttle';

import { cssMap, jsx } from '@atlaskit/css';
import { Inline } from '@atlaskit/primitives/compiled';
import { WidthObserver } from '@atlaskit/width-detector';

import { useChatModeStore } from '../../../../controllers/chat-mode-store/useChatModeStore';

const COMPACT_WIDTH_BREAKPOINT = 320;
const TINY_WIDTH_BREAKPOINT = 190;

const styles = cssMap({
	container: {
		position: 'relative',
	},
});

const useComponentsEnabled = (chatModeKey: string) => {
	const { deepResearchEnabled, thinkDeeperEnabled } = useChatModeStore(chatModeKey)[0];
	const { orgKnowledge, webSearchEnabled } = useChatModeStore(chatModeKey)[0];
	const hasFilteredApps = orgKnowledge !== 'disabled' && orgKnowledge.filteredApps.length > 0;

	return (
		Number(deepResearchEnabled) +
		Number(thinkDeeperEnabled) +
		Number(webSearchEnabled || hasFilteredApps)
	);
};

type Space = 'tiny' | 'compact' | 'large';

export const PillsWrapper = ({
	children,
	chatModeKey,
}: {
	children: (compact: boolean) => React.ReactNode;
	chatModeKey: string;
}): JSX.Element => {
	const [space, setSpace] = useState<Space>('compact');
	const [hideLabel, setHideLabel] = useState(false);
	const numberOfComponentsEnabled = useComponentsEnabled(chatModeKey);

	const checkWidth = useCallback((width: number) => {
		if (width < TINY_WIDTH_BREAKPOINT) {
			setSpace('tiny');
		} else if (width < COMPACT_WIDTH_BREAKPOINT) {
			setSpace('compact');
		} else {
			setSpace('large');
		}
	}, []);

	useLayoutEffect(() => {
		if (space === 'tiny') {
			setHideLabel(true);
		} else if (space === 'compact') {
			setHideLabel(numberOfComponentsEnabled > 1);
		} else {
			setHideLabel(false);
		}
	}, [numberOfComponentsEnabled, space]);

	const throttledSetContainerSpace = throttle(checkWidth, 50);

	return (
		<Inline space="space.075" xcss={styles.container} grow="fill">
			<WidthObserver setWidth={throttledSetContainerSpace} />
			{children(hideLabel)}
		</Inline>
	);
};
